<script lang="ts">
  import type { ButtonSize, ButtonType, ButtonVariant } from '$components/mds/Buttons';

  import {
    buttonVariantClasses,
    iconButtonWidthClasses,
    buttonHeightClasses,
    buttonTextClasses
  } from '$components/mds/Buttons';
  import LoadingSpinner from '$mds/LoadingSpinner';
  import IconWrapper, { IconWrapperSize, type IconComponentType } from '$components/mds/IconWrapper';
  import { type ActionArray, useActions } from '$components/actions';

  export let variant: ButtonVariant = 'primary';
  export let size: ButtonSize = 'md';
  export let icon: IconComponentType | null = null;
  export let iconClass: string = '';
  export let iconSize: IconWrapperSize | null = null;
  export let hasIndicator = false;
  export let tabindex: number | undefined = undefined;

  export let disabled = false;
  export let loading = false;
  $: isDisabled = disabled || loading;

  export let type: ButtonType = 'button';
  export let href: string | undefined = undefined;
  export let external = false;
  export let use: ActionArray = [];

  let className = '';
  export { className as class };

  const iconSizes: Record<ButtonSize, IconWrapperSize> = {
    xs: 'base',
    sm: 'base',
    md: 'md',
    lg: 'lg'
  };

  $: buttonProps = { type, disabled: isDisabled };
  const anchorProps = {
    href,
    role: 'button',
    ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})
  };
</script>

<svelte:element
  this={href ? 'a' : 'button'}
  {...href ? anchorProps : buttonProps}
  role={href ? 'link' : 'button'}
  {tabindex}
  class="relative flex shrink-0 items-center justify-center rounded-lg text-center transition-all duration-200 ease-in-out {isDisabled
    ? buttonVariantClasses[variant].disabled
    : buttonVariantClasses[variant].default} {buttonHeightClasses[size]} {iconButtonWidthClasses[
    size
  ]} {buttonTextClasses[size]} {className}"
  class:pointer-events-none={isDisabled}
  class:cursor-not-allowed={isDisabled}
  {...$$restProps}
  use:useActions={use}
  on:click
  on:keyup
  on:mouseenter
  on:mouseleave
  on:mousedown
  on:focus
  on:blur
>
  {#if loading}
    <LoadingSpinner theme={buttonVariantClasses[variant].spinner} />
  {:else if icon}
    <IconWrapper {icon} size={iconSize ?? iconSizes[size]} class={iconClass} />
  {:else}
    <slot name="icon" />
  {/if}

  {#if hasIndicator}
    <span class="bg-alert-onlight-base absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-2 ring-neutral-800" />
  {/if}

  <!-- a11y text -->
  <span class="sr-only">
    <slot />
  </span>
</svelte:element>
